import React from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/layout/NavBar';
import SiteFooter from '../components/layout/SiteFooter';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import FAQSection from '../components/FAQSection';
import { useWaitlistModal } from '../context/WaitlistModalContext';
import { organizationSchema, webPageSchema, breadcrumbSchema, faqSchema } from '../schema';
import { CONTACT_EMAIL, LEGAL_ADDRESS } from '../seoConfig';

const faqItems = [
  {
    question: 'Who is behind SaniQ?',
    answer: 'SaniQ is being developed by a small founding team focused on entry-point hygiene technology, ahead of a planned 2027 Australian launch. Founder and team profiles will be added to this page as launch approaches.',
  },
  {
    question: 'Is SaniQ available to buy today?',
    answer: 'Not yet, SaniQ is in pre-launch development. Join the waitlist for launch-day notifications and founding-member pricing.',
  },
  {
    question: 'Where will SaniQ launch first?',
    answer: 'SaniQ is targeting an Australian launch in 2027, with residential and commercial (workplace, healthcare, childcare) editions planned.',
  },
  {
    question: 'How can I get in touch with the SaniQ team?',
    answer: `Reach us at ${CONTACT_EMAIL} for press, partnership, or general enquiries.`,
  },
];

const About = () => {
  const { openWaitlistModal } = useWaitlistModal();

  return (
    <div className="relative w-full min-h-screen">
      <SEO
        title="About Us"
        description="Learn why SaniQ was built: the shoe-sole hygiene problem, our mission for entry-point disinfection, and our roadmap to a 2027 Australian launch."
        path="/about"
        schema={[
          webPageSchema({ title: 'About SaniQ', description: 'The mission and story behind SaniQ.', path: '/about' }),
          organizationSchema(),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]),
          faqSchema(faqItems),
        ]}
      />
      <NavBar />
      <Breadcrumbs trail={[{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]} />

      <header className="px-8 pb-12 pt-8 max-w-3xl mx-auto text-center">
        <p className="text-cyan-400 font-mono text-[10px] tracking-[0.4em] uppercase mb-4">About Us</p>
        <h1 className="text-4xl md:text-6xl font-extralight tracking-tighter text-white mb-6">
          Hygiene shouldn't stop at the doorstep.
        </h1>
        <p className="text-white/45 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
          SaniQ is building the world's first intelligent disinfectant door mat, because the biggest hygiene gap in most homes and workplaces is the one nobody thinks about: the floor at the front door.
        </p>
      </header>

      <section className="py-12 px-8 max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-light text-white mb-6">Our story</h2>
        <div className="space-y-5 text-white/50 text-sm md:text-base leading-relaxed">
          <p>
            SaniQ started from a simple observation: households spend enormous effort on hand hygiene, surface cleaning, and air quality, while the single highest-contact surface in the home, the entryway floor, is treated with nothing more than a woven mat that traps a fraction of what walks in on shoe soles.
          </p>
          <p>
            We set out to build a device that could do at the door what a doormat only pretends to do: actually eliminate pathogens, automatically, without asking anyone to change their behaviour.
          </p>
        </div>
      </section>

      <section className="py-12 px-8 max-w-5xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white text-center mb-12">The problem we're solving</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="glass rounded-3xl p-8 md:p-10 border border-white/10">
            <h3 className="text-lg font-medium text-white mb-4">Traditional Door Mats</h3>
            <p className="text-white/45 text-sm leading-relaxed mb-6">
              They trap visible dirt, but bacteria and viruses still pass through.
            </p>
            <ul className="text-white/50 text-sm space-y-3 list-disc list-inside marker:text-cyan-500">
              <li>No germ-killing capability</li>
              <li>Passive and ineffective</li>
              <li>False sense of cleanliness</li>
            </ul>
          </div>
          <div className="glass rounded-3xl p-8 md:p-10 border border-cyan-500/20">
            <h3 className="text-lg font-medium text-white mb-4">SaniQ: Intelligent Disinfection</h3>
            <p className="text-white/45 text-sm leading-relaxed mb-6">
              Eliminates pathogens at the entry point with medical-grade disinfectant and automated spray technology.
            </p>
            <ul className="text-white/50 text-sm space-y-3 list-disc list-inside marker:text-cyan-500">
              <li>Medical-grade pathogen elimination</li>
              <li>Fully automatic</li>
              <li>Real-time monitoring</li>
            </ul>
          </div>
        </div>
        <p className="text-center mt-8">
          <Link to="/features" className="text-cyan-400 text-xs font-bold uppercase tracking-widest hover:text-cyan-300 transition-colors">
            See how it works →
          </Link>
        </p>
      </section>

      <section className="py-12 px-8 max-w-4xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white mb-10 text-center">Who's building this (Experience &amp; Expertise)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass rounded-2xl p-6 border border-white/10">
            <p className="text-white text-sm font-medium mb-2">Founding team</p>
            <p className="text-white/45 text-xs leading-relaxed">Team bios, backgrounds, and credentials will be added here ahead of launch.</p>
          </div>
          <div className="glass rounded-2xl p-6 border border-white/10">
            <p className="text-white text-sm font-medium mb-2">Product development</p>
            <p className="text-white/45 text-xs leading-relaxed">Details on engineering partners, disinfectant safety testing, and certification pathways will be added here ahead of launch.</p>
          </div>
          <div className="glass rounded-2xl p-6 border border-white/10">
            <p className="text-white text-sm font-medium mb-2">Compliance &amp; safety</p>
            <p className="text-white/45 text-xs leading-relaxed">Relevant Australian standards and safety certifications SaniQ is pursuing will be listed here ahead of launch.</p>
          </div>
          <div className="glass rounded-2xl p-6 border border-white/10">
            <p className="text-white text-sm font-medium mb-2">Contact &amp; transparency</p>
            <p className="text-white/45 text-xs leading-relaxed">
              Based in {LEGAL_ADDRESS}. Reach us any time at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-400 hover:text-cyan-300 transition-colors">{CONTACT_EMAIL}</a>
              . Read our <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300 transition-colors">Privacy Policy</Link> and <Link to="/terms" className="text-cyan-400 hover:text-cyan-300 transition-colors">Terms of Service</Link>.
            </p>
          </div>
        </div>
      </section>

      <FAQSection heading="Frequently Asked Questions" items={faqItems} />

      <section className="py-16 px-8 text-center">
        <div className="max-w-2xl mx-auto glass rounded-3xl p-10">
          <h2 className="text-2xl md:text-3xl font-light text-white mb-4">Follow the story to launch.</h2>
          <p className="text-white/45 text-sm mb-8">Join the waitlist for updates as SaniQ moves toward its 2027 Australian launch.</p>
          <button
            type="button"
            onClick={openWaitlistModal}
            className="px-10 py-4 bg-cyan-500 text-black font-bold rounded-full text-sm uppercase tracking-widest hover:scale-105 transition-transform"
          >
            Register
          </button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default About;
